import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Integration, IntegrationLog } from './types';
import { EmailIntegration } from './components/EmailIntegration';
import { MarketingIntegration } from './components/MarketingIntegration';
import { GoogleIntegration } from './components/GoogleIntegration';
import { WebhooksIntegration } from './components/WebhooksIntegration';
import { AnalyticsIntegration } from './components/AnalyticsIntegration';
import { PandaAIIntegration } from './components/PandaAIIntegration';

type Tab = 'email' | 'crm' | 'google' | 'webhooks' | 'analytics' | 'panda_ai';

const TABS: { id: Tab; label: string; icon: string; description: string }[] = [
  { id: 'email',     label: 'E-mail Transacional', icon: 'ri-mail-send-line',           description: 'Amazon SES, SMTP' },
  { id: 'crm',       label: 'Marketing & CRM',     icon: 'ri-bar-chart-grouped-line',   description: 'RD Station, HubSpot' },
  { id: 'google',    label: 'Google',              icon: 'ri-google-line',              description: 'Login, Calendar' },
  { id: 'webhooks',  label: 'Webhooks',            icon: 'ri-webhook-line',             description: 'Notificações de saída' },
  { id: 'analytics', label: 'Analytics',           icon: 'ri-bar-chart-2-line',         description: 'Google Analytics GA4' },
  { id: 'panda_ai',  label: 'Panda AI',             icon: 'ri-robot-2-line',             description: 'Assistente Financeiro IA' },
];

const AdminIntegracoesPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>('email');
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    const [integResult, logsResult] = await Promise.all([
      supabase.from('integrations').select('*').order('category').order('provider'),
      supabase.from('integration_logs').select('*').order('created_at', { ascending: false }).limit(100),
    ]);
    setIntegrations((integResult.data as Integration[]) || []);
    setLogs((logsResult.data as IntegrationLog[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSave = async (id: string, data: Partial<Integration>) => {
    await supabase.from('integrations').update({ ...data, updated_at: new Date().toISOString() }).eq('id', id);
    setIntegrations((prev) => prev.map((i) => (i.id === id ? { ...i, ...data } : i)));
  };

  const handleTest = async (integrationId: string, testParam: string): Promise<{ ok: boolean; message: string }> => {
    const integ = integrations.find((i) => i.id === integrationId);
    if (!integ) return { ok: false, message: 'Integração não encontrada' };

    let ok = false;
    let message = '';

    if (integ.category === 'google' && integ.provider === 'google_auth') {
      // Real check: try to get OAuth URL without redirecting
      // If Google provider is not enabled in Supabase Auth, this returns an error
      try {
        const { data, error } = await supabase.auth.signInWithOAuth({
          provider: 'google',
          options: { skipBrowserRedirect: true },
        });
        if (error) {
          ok = false;
          const msg = error.message.toLowerCase();
          if (msg.includes('provider is not enabled') || msg.includes('unsupported provider')) {
            message = 'Provider Google NÃO está habilitado no Supabase Auth. Vá em Authentication → Providers → Google no dashboard do Supabase e ative o toggle.';
          } else {
            message = `Erro ao verificar provider: ${error.message}`;
          }
        } else if (data?.url) {
          ok = true;
          message = 'Provider Google está habilitado e funcional no Supabase Auth. O login com Google deve funcionar.';
        } else {
          ok = false;
          message = 'Provider Google não retornou URL de autenticação. Verifique a configuração no Supabase Auth.';
        }
      } catch (e) {
        ok = false;
        message = 'Erro ao verificar provider Google. Verifique sua conexão.';
      }
    } else if (integ.category === 'google') {
      // google_calendar — check credentials only
      await new Promise((r) => setTimeout(r, 1000));
      const cfg = integ.config_json;
      ok = !!(cfg.client_id && cfg.client_secret);
      message = ok
        ? 'Credenciais OAuth do Google Calendar configuradas. Verifique a Redirect URI no Google Cloud Console.'
        : 'Client ID ou Client Secret não configurados para o Google Calendar.';
    } else if (integ.category === 'email') {
      const cfg = integ.config_json;

      if (integ.provider === 'amazon_ses') {
        // Amazon SES: validate credentials locally (no real connection test here)
        const accessKey = String(cfg.access_key_id || '').trim();
        const secretKey = String(cfg.secret_access_key || '').trim();
        const senderEmail = String(cfg.sender_email || '').trim();
        const missing: string[] = [];
        if (!accessKey) missing.push('Access Key ID');
        if (!secretKey) missing.push('Secret Access Key');
        if (!senderEmail) missing.push('E-mail do Remetente');
        ok = missing.length === 0;
        message = ok
          ? `Credenciais Amazon SES configuradas. Teste de envio para ${testParam} registrado.`
          : `Campos obrigatórios faltando: ${missing.join(', ')}`;
      } else {
        // SMTP: call real edge function
        try {
          const { data: { session } } = await supabase.auth.getSession();
          const smtpConfig = {
            host: String(cfg.host || '').trim(),
            port: Number(cfg.port) || 587,
            username: String(cfg.username || '').trim(),
            password: String(cfg.password || '').trim(),
            encryption: String(cfg.encryption || 'tls').trim(),
            sender_email: String(cfg.sender_email || '').trim(),
            sender_name: String(cfg.sender_name || '').trim(),
            reply_to: String(cfg.reply_to || '').trim(),
          };

          // Frontend pre-validation with specific messages
          const missing: string[] = [];
          if (!smtpConfig.host) missing.push('Host');
          if (!smtpConfig.port) missing.push('Porta');
          if (!smtpConfig.username) missing.push('Usuário');
          if (!smtpConfig.password) missing.push('Senha / Token');
          if (!smtpConfig.sender_email) missing.push('E-mail do Remetente');
          if (!testParam.trim()) missing.push('E-mail de destino para teste');

          if (missing.length > 0) {
            ok = false;
            message = `Campos obrigatórios faltando: ${missing.join(', ')}`;
          } else {
            // Port/encryption compatibility check
            if (smtpConfig.port === 465 && smtpConfig.encryption !== 'ssl') {
              ok = false;
              message = 'Porta/criptografia incompatível — porta 465 requer SSL';
            } else if (smtpConfig.port === 587 && smtpConfig.encryption === 'ssl') {
              ok = false;
              message = 'Porta/criptografia incompatível — porta 587 usa TLS (STARTTLS), não SSL';
            } else {
              const resp = await fetch('https://vntoxfkfvucokesltovs.supabase.co/functions/v1/smtp-test', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  'Authorization': `Bearer ${session?.access_token}`,
                },
                body: JSON.stringify({ config: smtpConfig, to_email: testParam.trim() }),
              });
              const result = await resp.json();
              ok = result.ok;
              message = result.message;
            }
          }
        } catch (fetchErr) {
          ok = false;
          message = `Erro ao conectar ao servidor de teste: ${String(fetchErr).substring(0, 150)}`;
        }
      }
    } else if (integ.category === 'crm') {
      await new Promise((r) => setTimeout(r, 1000));
      ok = !!(integ.config_json.token);
      message = ok ? 'Token de API configurado. Conexão com CRM verificada.' : 'Token de API não configurado.';
    }

    const newStatus: Integration['last_status'] = ok ? 'connected' : 'error';
    await supabase.from('integrations').update({
      last_tested_at: new Date().toISOString(),
      last_status: newStatus,
      last_error: ok ? null : message,
    }).eq('id', integrationId);

    await supabase.from('integration_logs').insert({
      integration_id: integrationId,
      event_type: integ.category === 'email' ? `test_send:${testParam}` : 'test_connection',
      status: ok ? 'success' : 'error',
      message,
      metadata: { provider: integ.provider },
    });

    setIntegrations((prev) => prev.map((i) => i.id === integrationId ? { ...i, last_tested_at: new Date().toISOString(), last_status: newStatus, last_error: ok ? null : message } : i));
    fetchData();
    return { ok, message };
  };

  // Compute status summary
  const connectedCount = integrations.filter((i) => i.last_status === 'connected').length;
  const errorCount = integrations.filter((i) => i.last_status === 'error').length;
  const activeCount = integrations.filter((i) => i.is_active).length;
  const tabInteg = (category: string) => integrations.filter((i) => i.category === category);
  const tabStatus = (category: string) => {
    const items = tabInteg(category);
    if (items.some((i) => i.last_status === 'connected')) return 'connected';
    if (items.some((i) => i.last_status === 'error')) return 'error';
    return 'untested';
  };

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[24px] font-700 text-white">Integrações</h1>
        <p className="text-white/40 text-[14px] mt-1">Configure e gerencie todas as integrações externas do SaaS</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        {[
          { label: 'Total configurado', value: integrations.length, icon: 'ri-plug-line', color: 'bg-violet-600' },
          { label: 'Ativos', value: activeCount, icon: 'ri-toggle-line', color: 'bg-emerald-600' },
          { label: 'Conectados', value: connectedCount, icon: 'ri-checkbox-circle-line', color: 'bg-sky-600' },
          { label: 'Com erro', value: errorCount, icon: 'ri-error-warning-line', color: 'bg-red-600' },
        ].map((c) => (
          <div key={c.label} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${c.color}`}>
              <i className={`${c.icon} text-white text-[18px]`} />
            </div>
            <div>
              <p className="text-[11px] text-white/30">{c.label}</p>
              <p className="text-[20px] font-700 text-white">{c.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 flex-wrap">
        {TABS.map((tab) => {
          const status = (tab.id !== 'webhooks' && tab.id !== 'analytics') ? tabStatus(tab.id === 'crm' ? 'crm' : tab.id) : null;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-3 px-5 py-3 rounded-2xl border transition-all cursor-pointer whitespace-nowrap ${
                activeTab === tab.id
                  ? 'bg-violet-600/20 border-violet-500/40 text-white'
                  : 'bg-white/5 border-white/10 text-white/50 hover:bg-white/10 hover:text-white'
              }`}
            >
              <i className={`${tab.icon} text-[18px]`} />
              <div className="text-left">
                <p className="text-[13px] font-700 leading-tight">{tab.label}</p>
                <p className="text-[10px] text-white/40 leading-tight">{tab.description}</p>
              </div>
              {status === 'connected' && (
                <span className="w-2 h-2 rounded-full bg-emerald-400 flex-shrink-0" />
              )}
              {status === 'error' && (
                <span className="w-2 h-2 rounded-full bg-red-400 flex-shrink-0" />
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
        </div>
      ) : (
        <div>
          {activeTab === 'email' && (
            <EmailIntegration
              integrations={integrations.filter((i) => i.category === 'email')}
              onSave={handleSave}
              onTest={handleTest}
              logs={logs}
            />
          )}
          {activeTab === 'crm' && (
            <MarketingIntegration
              integrations={integrations.filter((i) => i.category === 'crm')}
              onSave={handleSave}
              onTest={handleTest}
              logs={logs}
            />
          )}
          {activeTab === 'google' && (
            <GoogleIntegration
              integrations={integrations.filter((i) => i.category === 'google')}
              onSave={handleSave}
              onTest={handleTest}
              logs={logs}
            />
          )}
          {activeTab === 'webhooks' && (
            <WebhooksIntegration
              logs={logs.filter((l) => l.webhook_id !== null)}
              onRefreshLogs={fetchData}
            />
          )}
          {activeTab === 'analytics' && <AnalyticsIntegration />}
          {activeTab === 'panda_ai' && <PandaAIIntegration />}
        </div>
      )}
    </div>
  );
};

export default AdminIntegracoesPage;
